// frontend/src/services/localRecording/uploader.js
//
// Bakgrundsuppladdare för lokal-först-inspelning. Tömmer IndexedDB-kön:
// laddar upp chunkar i chunk_index-ordning per session, raderar EN chunk först
// vid HTTP 2xx-kvittens (delete-on-confirm). Backoff vid fel. När en session är
// klar lokalt OCH dess kö är tömd → complete mot servern.
//
// Väcks av: intervall-tick, `online`-event, nya chunkar (wake()) och app-start.

import apiClient from '../../apiClient';
import {
  getUnconfirmedChunks,
  getSession,
  getAllSessions,
  getChunksForSession,
  updateChunk,
  deleteChunk,
  setServerSessionId,
  deleteSessionAndChunks,
} from './db';

const UPLOAD_ENDPOINT = '/recorder/sessions/upload_chunk'; // v2, upsert på client_session_id
const MAX_ATTEMPTS = 20;
const TICK_MS = 5000;

// Backoff: 2s, 4s, 8s, 16s, 30s (tak)
function backoffMs(attempts) {
  return Math.min(2000 * 2 ** Math.max(0, attempts - 1), 30000);
}

function isPermanentHttp(status) {
  // 4xx (utom 408/429) = klientfel som inte blir bättre av omförsök.
  return status && status >= 400 && status < 500 && status !== 408 && status !== 429;
}

let running = false;      // single-flight
let wantAnother = false;  // en wake kom in medan vi körde
let timer = null;
const listeners = new Set(); // callbacks: (event) => void

function emit(event) {
  for (const l of listeners) {
    try { l(event); } catch (_) { /* ignorera lyssnarfel */ }
  }
}

// Prenumerera på uppladdar-händelser:
//  { type: 'confirmed', clientSessionId, chunkIndex, text }
//  { type: 'permanent_failure', clientSessionId, chunkIndex, error }
//  { type: 'session_completed', clientSessionId, serverSessionId }
//  { type: 'pending_changed' }
export function subscribeUploader(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

async function uploadOne(chunk) {
  const session = await getSession(chunk.client_session_id);
  if (!session) {
    // Ingen session-post → föräldralös chunk, radera för att inte fastna.
    await deleteChunk(chunk.id);
    return;
  }

  const form = new FormData();
  form.append('file', chunk.blob, `chunk-${chunk.chunk_index}.wav`);
  form.append('client_session_id', chunk.client_session_id);
  form.append('chunk_index', String(chunk.chunk_index));
  form.append('is_final', chunk.is_final ? 'true' : 'false');
  if (session.template_id != null) form.append('template_id', String(session.template_id));
  if (session.session_name) form.append('session_name', session.session_name);

  await updateChunk(chunk.id, { upload_state: 'uploading' });

  try {
    const resp = await apiClient.post(UPLOAD_ENDPOINT, form, { timeout: 60000 });

    // Lazy-skapad server-session: spara mappningen.
    const serverId = resp.data?.server_session_id;
    if (serverId && !session.server_session_id) {
      await setServerSessionId(chunk.client_session_id, serverId);
    }

    // Extrahera ev. deltext (samma format som gamla transcribeChunk).
    let text = '';
    const tr = resp.data?.transcription;
    if (typeof tr === 'string') text = tr;
    else if (tr && typeof tr === 'object') text = tr.corrected_transcript || tr.raw_transcript || '';

    // Delete-on-confirm.
    await deleteChunk(chunk.id);
    emit({ type: 'confirmed', clientSessionId: chunk.client_session_id, chunkIndex: chunk.chunk_index, text });
    emit({ type: 'pending_changed' });
    return;
  } catch (err) {
    const status = err.response?.status;
    const attempts = (chunk.attempts || 0) + 1;

    if (isPermanentHttp(status) || attempts >= MAX_ATTEMPTS) {
      await updateChunk(chunk.id, {
        upload_state: 'permanent_failure',
        attempts,
        last_error: err.response?.data?.message || err.message,
      });
      emit({
        type: 'permanent_failure',
        clientSessionId: chunk.client_session_id,
        chunkIndex: chunk.chunk_index,
        error: err.response?.data?.message || err.message,
      });
      // Rapportera metadata till backend så admin ser det (audio finns bara lokalt).
      reportPermanentFailure(session, chunk, err).catch(() => {});
      emit({ type: 'pending_changed' });
      return;
    }

    // Övergående fel → backoff, försök igen senare.
    await updateChunk(chunk.id, {
      upload_state: 'pending',
      attempts,
      last_error: err.message,
      next_attempt_at: Date.now() + backoffMs(attempts),
    });
    throw err; // signalera till drainOnce att denna session inte kan gå vidare nu
  }
}

async function reportPermanentFailure(session, chunk, err) {
  try {
    await apiClient.post('/api/errors/failed_chunk', {
      client_session_id: chunk.client_session_id,
      server_session_id: session.server_session_id || null,
      chunk_index: chunk.chunk_index,
      attempts: chunk.attempts,
      last_error: err.response?.data?.message || err.message,
      timestamp: Date.now(),
    });
  } catch (_) { /* best effort */ }
}

// Försök avsluta en session mot servern när dess kö är tömd.
async function tryCompleteSession(clientSessionId) {
  const session = await getSession(clientSessionId);
  if (!session || session.status !== 'finished') return;

  const remaining = await getChunksForSession(clientSessionId);
  const stillPending = remaining.some((c) => c.upload_state !== 'permanent_failure');
  if (stillPending) return; // vänta tills allt (utom permanent döda) är bekräftat

  const serverId = session.server_session_id;
  if (!serverId) {
    // Alla chunkar permanent döda och ingen server-session skapades — inget att avsluta.
    if (remaining.length > 0) return;
  }

  try {
    if (serverId) {
      await apiClient.post(`/recorder/sessions/${serverId}/complete`, {});
    }
    emit({ type: 'session_completed', clientSessionId, serverSessionId: serverId || null });
    await deleteSessionAndChunks(clientSessionId);
    emit({ type: 'pending_changed' });
  } catch (_) {
    // Complete misslyckades (server nere) — försök igen nästa varv.
  }
}

async function drainOnce() {
  const chunks = await getUnconfirmedChunks();
  const now = Date.now();

  // Gruppera per session, behåll ordning. Hoppa över permanent döda.
  const bySession = new Map();
  for (const c of chunks) {
    if (c.upload_state === 'permanent_failure') continue;
    if (c.next_attempt_at && c.next_attempt_at > now) continue;
    if (!bySession.has(c.client_session_id)) bySession.set(c.client_session_id, []);
    bySession.get(c.client_session_id).push(c);
  }

  // Ladda upp sessioner parallellt, men chunkar inom en session i ordning
  // (stoppa sessionen vid första felet så ordningen bevaras).
  const sessionIds = [...bySession.keys()];
  await Promise.all(
    sessionIds.map(async (sid) => {
      for (const chunk of bySession.get(sid)) {
        try {
          await uploadOne(chunk);
        } catch (_) {
          return; // övergående fel — avbryt denna session, behåll ordning
        }
      }
    })
  );

  // Försök avsluta ALLA färdigmarkerade sessioner vars kö nu är tömd — inte bara
  // de som hade chunkar i detta varv (en session kan ha markerats klar efter att
  // dess sista chunk redan bekräftats, och skulle annars aldrig completas/städas).
  const allSessions = await getAllSessions();
  for (const s of allSessions) {
    if (s.status === 'finished') {
      await tryCompleteSession(s.client_session_id);
    }
  }
}

// Kör en drain; om en wake kom in under tiden, kör igen (single-flight).
async function runLoop() {
  if (running) { wantAnother = true; return; }
  running = true;
  try {
    do {
      wantAnother = false;
      await drainOnce();
    } while (wantAnother);
  } finally {
    running = false;
  }
}

// Väck uppladdaren (ny chunk, manuell "skicka igen", online-event).
export function wakeUploader() {
  runLoop().catch((e) => console.error('uploader runLoop error', e));
}

let started = false;
// Montera uppladdaren globalt (t.ex. vid app-start). Idempotent.
export function startUploader() {
  if (started) return;
  started = true;
  window.addEventListener('online', wakeUploader);
  timer = setInterval(wakeUploader, TICK_MS);
  wakeUploader(); // återuppta leftover-chunkar direkt
}

export function stopUploader() {
  if (timer) clearInterval(timer);
  window.removeEventListener('online', wakeUploader);
  timer = null;
  started = false;
}
